import {Component, computed, OnInit} from '@angular/core';
import {Button} from 'primeng/button';
import {DatePipe} from '@angular/common';
import {ManageTaBaseComponent} from '../manage-ta-base-component.directive';
import {TaCostBubbleComponent} from '../../ta-cost-bubble/ta-cost-bubble.component';
import {ApprovalContext} from '../../../../api';
import {
  ItineraryDifferenceComparisonComponent
} from '../itinerary-difference-comparison/itinerary-difference-comparison.component';
import {TaApprovalsTableComponent} from '../ta-approvals-table/ta-approvals-table.component';
import {
  ManageTaPaymentActionsComponent
} from '../manage-ta-payment-actions/manage-ta-payment-actions.component';
import {toTAStatusText} from '../../../../util/app.types';
import {resolveItineraryTravellerId} from '../../../../util/general.utils';

/**
 * The manage page for a CancelApproved TA - a pre-paid TA whose cancellation has been approved and
 * now only needs finance to close it out, landing on the ordinary Cancelled status. The counterpart
 * of manage-ta-finalise, which handles the same step for an approved acquittal.
 *
 * Finalise is the only way forward: the server's resetFinalise refuses a CancelApproved TA, so
 * there is no withholding this one back to an earlier stage. The way out is to reverse the
 * cancellation entirely, which finance/admin can still do from here (AdminController.RevertPrepayCancel).
 */
@Component({
  selector: 'manage-ta-cancel-finalise',
  imports: [
    Button,
    DatePipe,
    TaCostBubbleComponent,
    ItineraryDifferenceComparisonComponent,
    TaApprovalsTableComponent,
    ManageTaPaymentActionsComponent,
  ],
  templateUrl: './manage-ta-cancel-finalise.component.html',
  styleUrl: './manage-ta-cancel-finalise.component.scss'
})
export class ManageTaCancelFinaliseComponent extends ManageTaBaseComponent implements OnInit {
  // Cancellation sits outside the approval chain - this lists the sign-off that got the TA here.
  readonly cancellationContexts: ApprovalContext[] = ['Cancellation'];

  // Read straight off the TA rather than the base component's estimate-first computeds. No estimate
  // is fetched for a cancelled TA (see manage-ta-container), and a store.taValue left over from
  // before the cancellation would still be carrying the pre-cancel figures. The TA's own values are
  // the ones the server derived from the itinerary versions: v0 for original, the zero version for
  // current.
  override originalTaValue = computed<number>(() => this.taSubmission()?.originalValue ?? 0);
  override currentTaValue = computed<number>(() => this.taSubmission()?.currentValue ?? 0);
  override prePaidTaValue = computed<number | null>(() => this.taSubmission()?.prePaidValue ?? null);
  override currentFinanceValue = computed<number>(() => this.taSubmission()?.currentFinanceValue ?? this.currentTaValue());

  canFinaliseCancellation = computed<boolean>(() => this.permissionService.canFinaliseCancellation());

  // Undoing a cancellation raised in error. Independent of the action above - the reverser is
  // finance/admin correcting the record, not an approver of this TA.
  canRevertCancellation = computed<boolean>(() => this.permissionService.canRevertCancellation());

  // Cancelling zeroed the TA's itinerary, so the change in value is the prepayment to be recovered.
  amountToRecover = computed<number>(() => -this.changeInTaValue());

  // The deduction itself is offered by manage-ta-payment-actions; this only reassures the finaliser
  // that a deduction already recorded has been taken care of - same as manage-ta-finalise.
  isDeductionProcessed = computed<boolean>(() => (
    (this.taSubmission()?.approvals ?? []).some(approval =>
      approval.actionContext === 'OverPayment' && approval.status === 'Actioned')
  ));

  // The itinerary comparison reads both the current itinerary and the full itinerary list, and this
  // page is reachable directly on refresh, so it owns the same fetches as the other manage pages.
  override ngOnInit() {
    super.ngOnInit();
    // Falls back past the primary traveller so an all-casual delegate TA - which has none - still
    // resolves to a traveller whose itinerary can be fetched.
    const id = resolveItineraryTravellerId(this.taSubmission(), this.store.user()?.id);
    if (id) {
      this.taSubmissionDatasource.loadTravellerItinerary(id);
    }

    this.taSubmissionDatasource.getAllItineraries();
  }

  confirmReverseCancellation(): void {
    this.confirmationServiceWrapper.revertCancellationConfirm(this.taSubmission()).subscribe({
      next: () => {
        this.goBack();
      },
      error: err => {
        console.error(err);
      },
    });
  }

  confirmFinaliseCancellation(): void {
    this.confirmationServiceWrapper.finaliseCancellationConfirm('', this.taSubmission()).subscribe({
      next: () => {
        this.goBack();
      },
      error: err => {
        console.error(err);
      },
    });
  }

  protected readonly toTAStatusText = toTAStatusText;
}
